import React from 'react';
import { Box, Card, CardContent, Typography, Tooltip, keyframes } from '@mui/material';

const whalePulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.15); opacity: 0.85; }
  100% { transform: scale(1); opacity: 1; }
`;

const GEXHeatmap = ({ heatmapData, spyPrice, gammaFlip, flowAlerts = [], showWhales = false }) => {
    // Ensure data is valid to prevent hook crashes
    const data = React.useMemo(() => heatmapData || [], [heatmapData]);

    const expirations = React.useMemo(() => {
        if (data.length === 0) return [];
        return Object.keys(data[0]).filter((k) => k !== 'strike');
    }, [data]);

    // === DYNAMIC PHYSICS-BASED LIGHTING MODEL ===
    // Calibrate maximum intensity to the highest value in the current dataset.
    // This ensures physics lighting works across all tickers (SPY to IBIT).
    const maxGex = React.useMemo(() => {
        if (data.length === 0) return 1000000;
        let max = 0;
        data.forEach(row => {
            expirations.forEach(exp => {
                const val = Math.abs(row[exp] || 0);
                if (val > max) max = val;
            });
        });
        // Default to $1M to prevent division by zero or excessive gain on empty data
        return Math.max(max, 1000000);
    }, [data, expirations]);

    if (data.length === 0) {
        return <Typography>No data available</Typography>;
    }

    const getCellStyle = (rawValue) => {
        const value = Number(rawValue);
        if (!value || value === 0) {
            return {
                backgroundColor: 'rgba(15, 23, 42, 0.4)',
                color: 'rgba(255,255,255,0.3)',
                boxShadow: 'none',
                border: 'none',
                fontWeight: 400
            };
        }

        const absValue = Math.abs(value);

        // 1. NORMALIZE (0.0 to 1.0) based on Dynamic Range
        let t = Math.min(absValue / maxGex, 1.0);

        // 2. NOISE GATE (Relative)
        // 0.5% of the max value is treated as noise.
        // For SPY ($3B max) this is $15M. For IBIT ($100M max) this is $500k.
        if (t < 0.005) {
            return {
                backgroundColor: 'rgba(15, 23, 42, 0.6)', // Very dark
                color: 'rgba(255,255,255,0.2)',
                boxShadow: 'none',
                border: 'none',
                fontWeight: 400
            };
        }

        // 3. CURVE (Gamma Correction)
        // Adjusted power from 0.7 to 0.55 to boost low-end visibility.
        const intensity = Math.pow(t, 0.55);

        // 4. COLOR PHYSICS (Hue Shift)
        let h, s, l;

        if (value > 0) {
            // CALLS
            const startH = 150;
            const endH = 165;
            h = startH + (endH - startH) * intensity;

            // Saturation: 30% -> 100% (More color at low end)
            s = 30 + (intensity * 70);

            // Lightness: 18% -> 70% (Brighter low end start)
            l = 18 + (intensity * 52);
        } else {
            // PUTS
            const startH = 355; // Red
            const endH = 325;   // Deep Pink/Magenta
            h = startH + (endH - startH) * intensity;

            s = 30 + (intensity * 70);
            l = 20 + (intensity * 50); // Red needs slightly more base luminance
        }

        const colorString = `hsl(${h}, ${s}%, ${l}%)`;

        // 5. OPTICAL GLOW (Box Shadow)
        // High energy items emit light. We simulate this with box-shadow.
        // Only activates for intensity > 50%
        let boxShadow = 'none';
        let zIndex = 1;
        let border = 'none';

        if (intensity > 0.5) {
            const glowRadius = (intensity - 0.5) * 20; // 0px to 10px
            const glowAlpha = (intensity - 0.5) * 0.8; // 0.0 to 0.4 opacity

            boxShadow = `0 0 ${glowRadius}px hsla(${h}, 100%, 70%, ${glowAlpha})`;
            zIndex = 2; // Pop above neighbors
        }

        // 6. NUCLEAR BORDER (Top 10% of range)
        // If value is > 90% of maxGex, give it the nuclear treatment.
        if (t > 0.9) {
            border = `1px solid hsla(${h}, 100%, 85%, 0.8)`;
            boxShadow += `, inset 0 0 10px hsla(${h}, 100%, 70%, 0.5)`;
            zIndex = 5;
        }

        // 7. DYNAMIC TEXT CONTRAST
        const textColor = l > 60 ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.95)';
        // Bold if > 50% intensity
        const fontWeight = intensity > 0.5 ? 800 : 500;

        return {
            backgroundColor: colorString,
            color: textColor,
            boxShadow,
            border,
            fontWeight,
            zIndex,
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s',
            position: 'relative'
        };
    };

    const getFlowAlerts = (strike, expiry) => {
        const matches = flowAlerts.filter(a =>
            Math.abs(a.strike - strike) < 0.1 &&
            a.expiry === expiry
        );

        if (matches.length === 0) return [];

        // GOD TIER REFINEMENT: Only show the single most confident alert (Dominant Signal)
        // This prevents "muddy" signals when both sides are unusually active.
        return [matches.sort((a, b) => b.confidence - a.confidence)[0]];
    };

    const formatValue = (val) => {
        if (!val || val === 0) return '';
        const absVal = Math.abs(val);
        if (absVal >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
        if (absVal >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
        if (absVal >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
        return `$${val.toFixed(0)}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };


    // === CALCULATE GAMMA FLIP ===
    // Priority 1: Use the gammaFlip passed from backend (standardized to 0DTE/Nearest)
    // Priority 2: Recalculate based on the FIRST column only (Matches cards)
    let flipIndex = -1;

    if (gammaFlip) {
        const targetIdx = heatmapData.findIndex(row => Math.abs(row.strike - gammaFlip) < 0.1);
        if (targetIdx > 0) {
            // Data is sorted high-to-low (descending strikes).
            // To put the line at the 591/590 boundary where 590 is the flip strike,
            // we anchor to the row ABOVE 590 (index of 591) and use bottom: 0.
            flipIndex = targetIdx - 1;
        } else {
            flipIndex = targetIdx;
        }
    }

    // Fallback if no prop or strike not found (though backend should match)
    if (flipIndex === -1) {
        // Calculate based only on the FIRST expiration (0DTE focus)
        const firstExp = expirations[0];
        for (let i = 0; i < heatmapData.length - 1; i++) {
            const current = heatmapData[i][firstExp] || 0;
            const next = heatmapData[i + 1][firstExp] || 0;
            if ((current >= 0 && next < 0) || (current < 0 && next >= 0)) {
                flipIndex = i;
                break;
            }
        }
    }

    return (
        <Card sx={{
            background: 'rgba(30, 41, 59, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 2
        }}>
            <CardContent>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, mb: 2, display: 'block' }}>
                    🔥 GEX Heatmap
                </Typography>

                <Box sx={{
                    overflowX: 'auto',
                    pb: 2,
                    /* God Tier Hover-Only Scrollbar */
                    '&::-webkit-scrollbar': {
                        height: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: '10px',
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.25)',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.4)',
                    }
                }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `120px repeat(${expirations.length}, 140px)`,
                            gap: 1,
                            minWidth: 'fit-content',
                            padding: 1
                        }}
                    >
                        <Box sx={{ p: 1.5, fontWeight: 700, color: 'text.secondary', fontSize: '0.875rem', textAlign: 'center' }}>Strike</Box>
                        {expirations.map((exp) => (
                            <Box key={exp} sx={{ p: 1.5, fontWeight: 700, color: 'text.secondary', fontSize: '0.875rem', textAlign: 'center' }}>
                                {formatDate(exp)}
                            </Box>
                        ))}

                        {heatmapData.map((row, idx) => {
                            // === SMOOTH PRICE CURSOR LOGIC ===
                            // Find if this row is the "Ceiling" (Price is between this strike and the next)
                            const nextRow = heatmapData[idx + 1];
                            let showCursor = false;
                            let cursorTop = '50%';

                            // Check if price falls in the interval [thisStrike, nextStrike)
                            if (spyPrice <= row.strike && nextRow && spyPrice > nextRow.strike) {
                                showCursor = true;
                                const totalDiff = row.strike - nextRow.strike; // e.g. 1.0
                                const currentDiff = row.strike - spyPrice;     // e.g. 0.5
                                const ratio = currentDiff / totalDiff;         // 0.5

                                // Interpolate position: 50% (middle of this row) -> Middle of next row
                                // Gap approximation: 100% of height + 8px gap
                                cursorTop = `calc(50% + (${ratio} * (100% + 8px)))`;
                            }
                            // Fallback: If price is exactly equal to strike (or floating point close)
                            else if (Math.abs(spyPrice - row.strike) < 0.01) {
                                showCursor = true;
                                cursorTop = '50%';
                            }

                            return (
                                <React.Fragment key={idx}>
                                    <Tooltip title={`Strike: $${row.strike}`}>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                fontWeight: 600,
                                                backgroundColor: 'rgba(15, 23, 42, 0.3)',
                                                borderRadius: 1,
                                                color: 'text.primary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative',
                                                zIndex: showCursor ? 30 : 1, // Bring active row forward
                                                overflow: 'visible' // Allow cursor to float out
                                            }}
                                        >
                                            ${row.strike.toFixed(0)}

                                            {/* SMOOTH PRICE CURSOR */}
                                            {showCursor && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: cursorTop,
                                                    left: 0,
                                                    right: 0,
                                                    height: '2px', // Minimalist line
                                                    bgcolor: '#fbbf24',
                                                    boxShadow: '0 0 6px #fbbf24, 0 0 12px rgba(251, 191, 36, 0.5)', // Strong glow
                                                    zIndex: 40,
                                                    transform: 'translateY(-50%)',
                                                    pointerEvents: 'none',
                                                    transition: 'top 0.5s linear', // Fluid movement

                                                    // Add a small "Anchor" dot on the left to ground it
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        left: '4px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        width: '4px',
                                                        height: '4px',
                                                        borderRadius: '50%',
                                                        bgcolor: '#fff', // White core for contrast
                                                        boxShadow: '0 0 4px #fbbf24'
                                                    }
                                                }}>
                                                    {/* LIVE PRICE LABEL */}
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        left: '-10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        bgcolor: '#fbbf24',
                                                        color: '#0f172a',
                                                        px: 0.5,
                                                        py: 0,
                                                        borderRadius: '3px',
                                                        fontSize: '0.6rem',
                                                        fontWeight: 800,
                                                        lineHeight: 1.2,
                                                        boxShadow: '0 0 5px rgba(251, 191, 36, 0.5)'
                                                    }}>
                                                        ${spyPrice.toFixed(2)}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Tooltip>

                                    {expirations.map((exp, colIdx) => {
                                        const value = row[exp] || 0;
                                        const styles = getCellStyle(value);
                                        const alertsForCell = getFlowAlerts(row.strike, exp);

                                        // Gamma Flip Logic:
                                        const isFlipRow = idx === flipIndex;
                                        const showFlipAnchor = isFlipRow && colIdx === 0;

                                        return (
                                            <Box key={exp} sx={{ position: 'relative' }}>
                                                <Tooltip
                                                    title={
                                                        <Box sx={{ p: 0.5 }}>
                                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, mb: 0.5 }}>
                                                                Strike: ${row.strike} | {formatDate(exp)}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '0.7rem' }}>
                                                                GEX: {formatValue(value)}
                                                            </Typography>
                                                            {alertsForCell.map((alert, idx) => (
                                                                <Box key={idx} sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                                                    <Typography sx={{
                                                                        color: alert.type === 'Call' ? '#00d4aa' : '#ff6b6b',
                                                                        fontWeight: 900,
                                                                        fontSize: '0.65rem'
                                                                    }}>
                                                                        ⚠️ {alert.type === 'Call' ? 'BULLISH CALL FLOW' : 'BEARISH PUT FLOW'}
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.65rem' }}>
                                                                        Vol: {alert.volume.toLocaleString()} | OI: {alert.open_interest.toLocaleString()}
                                                                    </Typography>
                                                                    <Typography sx={{ fontSize: '0.65rem', color: '#fff', opacity: 0.8 }}>
                                                                        Confidence: {alert.confidence.toFixed(1)}x OI
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    }
                                                >
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 1.5,
                                                            textAlign: 'center',
                                                            fontSize: '0.875rem',
                                                            cursor: 'pointer',
                                                            ...styles,
                                                            position: 'relative',
                                                            '&:hover': {
                                                                transform: 'scale(1.1)',
                                                                zIndex: 20,
                                                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.6), 0 0 15px rgba(255, 255, 255, 0.2)'
                                                            },
                                                        }}
                                                    >
                                                        {formatValue(value)}

                                                        {/* WHALE FLARE ICONS */}
                                                        {showWhales && alertsForCell
                                                            .filter(alert => (alert.significance_score || 0) >= 5.0)
                                                            .map((alert, idx) => {
                                                                const significance = alert.significance_score || 0;
                                                                const type = alert.type;
                                                                const color = type === 'Call' ? '#00d4aa' : '#ff6b6b';

                                                                // WHALE 3.0: Calculate Thermal Decay based on momentum and age
                                                                const flowMomentum = alert.flow_momentum || 0;
                                                                const detectedAt = alert.detected_at;
                                                                let minutesElapsed = 0;
                                                                let isFresh = false;
                                                                if (detectedAt) {
                                                                    try {
                                                                        const detected = new Date(detectedAt);
                                                                        const diff = Date.now() - detected.getTime();
                                                                        minutesElapsed = diff / 60000;
                                                                        isFresh = minutesElapsed > -1 && minutesElapsed < 5; // Allow 1min drift
                                                                    } catch (e) { /* ignore */ }
                                                                }

                                                                // Calculate velocity-based opacity decay
                                                                const velocity = 1.0 / (1.0 + 0.1 * minutesElapsed);
                                                                const opacityDecay = Math.max(0.4, velocity); // Floor at 40% opacity

                                                                // Base Whale 2.0 Tiered Styling (Size by Significance)
                                                                let baseSize = 16;
                                                                let fontSize = '9px';
                                                                let glow = `0 0 5px ${color}`;
                                                                let animation = 'none';
                                                                let border = '1px solid rgba(255,255,255,0.7)';

                                                                if (significance >= 50) {
                                                                    baseSize = 24;
                                                                    fontSize = '14px';
                                                                    border = '2px solid #fff';
                                                                } else if (significance >= 20) {
                                                                    baseSize = 20;
                                                                    fontSize = '11px';
                                                                    border = '1.5px solid rgba(255,255,255,0.9)';
                                                                }

                                                                // WHALE 3.0: Momentum-based Glow and Animation
                                                                if (flowMomentum > 100) {
                                                                    // High Momentum: Intense pulsing
                                                                    glow = `0 0 20px ${color}, 0 0 40px ${color}66`;
                                                                    animation = `${whalePulse} 1s infinite ease-in-out`;
                                                                } else if (flowMomentum > 20) {
                                                                    // Medium Momentum: Subtle pulse
                                                                    glow = `0 0 12px ${color}`;
                                                                    animation = `${whalePulse} 2s infinite ease-in-out`;
                                                                } else if (isFresh) {
                                                                    // Fresh but low momentum: Still show some energy
                                                                    glow = `0 0 10px ${color}`;
                                                                    animation = `${whalePulse} 2.5s infinite ease-in-out`;
                                                                }

                                                                // Apply velocity decay to size
                                                                const size = baseSize * (0.7 + 0.3 * velocity);

                                                                return (
                                                                    <Box key={idx} sx={{
                                                                        position: 'absolute',
                                                                        top: `-${size / 3}px`,
                                                                        right: `-${size / 3}px`,
                                                                        bgcolor: color,
                                                                        color: '#fff',
                                                                        borderRadius: '50%',
                                                                        width: `${size}px`,
                                                                        height: `${size}px`,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        fontSize: fontSize,
                                                                        boxShadow: glow,
                                                                        animation: animation,
                                                                        zIndex: 10 + idx,
                                                                        border: border,
                                                                        opacity: opacityDecay,
                                                                        transition: 'all 0.5s ease',
                                                                        '@keyframes whalePulse': {
                                                                            '0%': { transform: 'scale(1)', opacity: opacityDecay },
                                                                            '50%': { transform: 'scale(1.15)', opacity: opacityDecay * 0.85 },
                                                                            '100%': { transform: 'scale(1)', opacity: opacityDecay }
                                                                        }
                                                                    }}>
                                                                        🐋
                                                                    </Box>
                                                                );
                                                            })}
                                                    </Box>
                                                </Tooltip>

                                                {/* === EVENT HORIZON (ABSOLUTE LASER) === */}
                                                {/* We anchor this entirely to the first expiration cell of the flip row */}
                                                {showFlipAnchor && (
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,       // Start strictly at the left edge of this cell (First Data Column)
                                                        right: 0,      // Extend to right edge of THIS cell
                                                        height: '2px', // Thin laser beam
                                                        background: 'linear-gradient(90deg, #a855f7 0%, #d8b4fe 100%)', // Sharp start, no diffuse fade-in
                                                        boxShadow: '0 0 10px #a855f7, 0 0 20px #a855f7',
                                                        pointerEvents: 'none', // Click-through
                                                        zIndex: 25,
                                                        transform: 'translateY(50%)' // Center exactly on the grid line
                                                    }}>
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            top: '50%', // Vertically center ON the line
                                                            left: '50%', // Center horizontally
                                                            transform: 'translate(-50%, -50%)', // precise centering
                                                            color: '#d8b4fe',
                                                            bgcolor: '#0f172a', // '#0f172a'
                                                            border: '0px solid #a855f7',
                                                            borderRadius: '4px',
                                                            px: 0.5,
                                                            fontSize: '0.45rem', // Slightly smaller to fit "GAMMA FLIP"
                                                            fontWeight: 800,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.05em',
                                                            boxShadow: '0 0 8px rgba(168, 85, 247, 0.6)',
                                                            whiteSpace: 'nowrap' // Ensure it doesn't wrap
                                                        }}>
                                                            GAMMA FLIP
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default GEXHeatmap;
